import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { Context } from "context/Wrapper";
import { collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "config/config";
import AdvancedTable from "components/AdvancedTable";
import tableConfig from "config/advancedTableColumnsControl.json";
import { getContactBaseColumns, getContactDateUpdatedColumn } from "config/contactColumns";
import useCustomParams from "components/shared/useCustomParams";



export const DashboardContactsTable = () => {
  const context = useContext(Context);
  const navigate = useNavigate();
  const { groupId, isPro, userId } = context?.state?.userProfile || {};
  const { showAllDataFlag } = context?.state || {};
  const { params } = useCustomParams();

  const tableId = "dashboard-contacts";
  const pageSize = Number(params.get(`${tableId}_pageSize`)) || 10;
  const page = Number(params.get(`${tableId}_page`)) || 0;
  const fetchLimit = (page + 1) * pageSize;

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentContacts = async () => {
      try {
        setLoading(true);
        const contactsRef = collection(db, "contacts");

        // Build query conditions
        const conditions = [];
        if (showAllDataFlag && groupId) {
          conditions.push(where("groupId", "==", groupId));
        } else if (!showAllDataFlag && userId) {
          conditions.push(where("userId", "==", userId));
        }
        conditions.push(orderBy("dateUpdated", "desc"));
        conditions.push(limit(fetchLimit));


        const q = query(contactsRef, ...conditions);
        const snapshot = await getDocs(q);

        const contactsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        setContacts(contactsData);
      } catch (error) {
        console.error("Error fetching contacts:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchRecentContacts();
    }
  }, [userId, showAllDataFlag, groupId, isPro, page, pageSize]);

  const dateUpdatedColumn = {
    field: "dateUpdated",
    headerName: "Last Updated",
    renderCell: ({ row }) => {
      if (!row.dateUpdated) return "";
      const d = typeof row.dateUpdated === "number"
        ? new Date(row.dateUpdated)
        : row.dateUpdated.toDate();
      return d.toISOString().split("T")[0];
    },
  };

  const columns = [
    ...getContactBaseColumns(),
    ...(tableConfig.contacts.dateUpdated ? [getContactDateUpdatedColumn()] : []),
  ];
  const safeContactExtraFilters = {
    architect: false,
    client: false,
    contractor: false,
    employee: false,
    engineer: false,
    filingRep: false,
    owner: false,
    tenant: false
  };

  if (loading) {
    return <Typography>Loading contacts...</Typography>;
  }

  return (
    <Box sx={{
      '& .MuiDataGrid-toolbarContainer': {
        display: 'none !important'
      },
      '& .MuiTablePagination-actions': {
        display: 'none !important'
      },
      '& .MuiTablePagination-displayedRows': {
        display: 'none !important'
      },
      '& .MuiTablePagination-selectLabel': {
        display: 'block !important',
        marginRight: '8px !important'
      },
      '& .MuiTablePagination-select': {
        display: 'block !important'
      },
      '& .MuiTablePagination-toolbar': {
        minHeight: '52px !important',
        paddingRight: '16px !important'
      }
    }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Recent Contacts</Typography>
      <AdvancedTable
        data={contacts}
        collectionName="contacts"
        columnsRaw={columns}
        rowCount={contacts.length}
        isInactive={false}
        contactExtraFilters={safeContactExtraFilters}
        tableId="dashboard-contacts"
        pageSize={10}
        hideActions={true}
        disableSorting={true}
        disableColumnMenu={true}

      />
    </Box>
  );
};